import { useEffect, useState } from "react";
import { gameName } from "../../lib/gameName";
import { Link } from "react-router-dom";
import { ImagePlus, Lock, Play, Ticket, Trophy } from "lucide-react";
import { DAYS, LiveLamp } from "./ClubChrome";

const chips = (n) => Number(n || 0).toLocaleString();
const TODAY = () => DAYS[(new Date().getDay() + 6) % 7][0];
// "19:30" as the club writes it on the board.
const clockLabel = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")}${h < 12 ? "am" : "pm"}`;
};

export const GAME_LABELS = {
  HOLDEM: "Hold'em", PLO: "PLO", PLO5: "PLO 5-Card", PLO6: "PLO 6-Card", OMAHA_HILO: "Omaha Hi/Lo",
  STUD: "Seven Card Stud", STUD_HILO: "Stud Hi/Lo", RAZZ: "Razz", DRAW_27_TRIPLE: "2-7 Triple Draw",
  FIVE_CARD_DRAW: "Five Card Draw", PINEAPPLE: "Crazy Pineapple",
};

// "Monday 20 Oct" — but Today and Tomorrow say so.
const whenLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const t = new Date();
  const days = Math.round((d - new Date(t.getFullYear(), t.getMonth(), t.getDate())) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
};

// A table the club has set up in their own room.
const ClubGameRow = ({ g, signedIn, i }) => (
  <div data-testid={`club-game-${i}`} className="club-row px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="club-display text-lg uppercase leading-none club-neon">{g.name}</span>
        <span className="club-badge px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">{whenLabel(g.date)}</span>
      </div>
      <span className="text-[11px] club-soft">
        {GAME_LABELS[g.game] || g.game} · {g.seats}-max · {g.time}
      </span>
      {g.prize && <span className="text-[11px] club-accent-text flex items-center gap-1"><Trophy className="w-3 h-3" /> {g.prize}</span>}
    </div>
    <div className="flex items-center gap-4 shrink-0">
      <div className="text-right">
        <div className="club-display text-lg uppercase leading-none">{g.free ? "Free" : g.buyin || "—"}</div>
        <div className="text-[9px] uppercase tracking-[0.2em] club-soft">{g.free ? "Freeroll" : "Buy-in"}</div>
      </div>
      <Link to={signedIn ? `#` : "/"} onClick={(e) => signedIn && e.preventDefault()} data-testid={`club-game-cta-${i}`}
        className={`club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em] ${signedIn ? "opacity-70 cursor-default" : ""}`}>
        <Ticket className="w-3.5 h-3.5" /> {signedIn ? "Rego 1hr before" : "Sign in"}
      </Link>
    </div>
  </div>
);

const Panel = ({ title, note, children, className = "", bodyClass = "", testId }) => (
  <div className={`club-panel flex flex-col min-h-0 ${className}`} data-testid={testId}>
    <span className="club-bulbs club-bulbs-top" />
    <div className="club-panel-head px-4 py-3 flex items-end justify-between gap-3 shrink-0">
      <h2 className="club-display text-base md:text-xl uppercase leading-none club-neon">{title}</h2>
      {note && <span className="text-[10px] uppercase tracking-[0.18em] club-soft">{note}</span>}
    </div>
    <div className={`club-scroll p-4 flex flex-col gap-3 min-h-0 ${bodyClass}`}>{children}</div>
  </div>
);

const ClubEventRow = ({ ev, mtt, signedIn, onRegister, onStart, busy }) => {
  const inst = mtt?.instance;
  const mine = inst?.registered_me;
  const seat = inst?.my_table_id;
  const opensAt = mtt?.next_at;
  const startable = !inst && signedIn && ev.on_demand;   // on-demand: START opens it with a 3-minute countdown
  const waiting = !inst && signedIn && !ev.on_demand;
  return (
    <div data-testid={`club-event-${ev.event_id}`}
      className="club-row px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="club-display text-lg uppercase leading-none club-neon">{ev.name}</span>
          {ev.run_date && <span className="club-badge px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">{whenLabel(ev.run_date)}</span>}
          {ev.invite_only && (
            <span className="club-badge flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">
              <Lock className="w-3 h-3" /> Invite only
            </span>
          )}
        </div>
        <span className="text-[11px] club-soft">
          {gameName(ev, true)} · {ev.max_seats ? `${ev.max_seats}-max · ` : ""}
          {ev.on_demand ? "Starts when you press START · late rego open" : <>{(ev.times || []).map(clockLabel).join(" · ") || "Times to be set"} {ev.tz_label}</>}
          {inst ? ` · ${inst.registered ?? 0} registered` : ""}
        </span>
        {ev.club_prize && (
          <span className="text-[11px] club-accent-text flex items-center gap-1">
            <Trophy className="w-3 h-3" /> {ev.club_prize}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-right">
          <div className="club-display text-lg uppercase leading-none">{ev.free ? "Free" : chips(ev.buy_in)}</div>
          <div className="text-[9px] uppercase tracking-[0.2em] club-soft">{ev.free ? "Freeroll" : `+ ${ev.fee_percent}% fee`}</div>
        </div>

        {!signedIn && (
          <Link to="/" data-testid={`club-event-cta-${ev.event_id}`}
            className="club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em]">
            <Ticket className="w-3.5 h-3.5" /> Sign in
          </Link>
        )}
        {signedIn && seat && (
          <Link to={`/table/${seat}`} data-testid={`club-event-seat-${ev.event_id}`}
            className="club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em]">
            <Ticket className="w-3.5 h-3.5" /> Take your seat
          </Link>
        )}
        {signedIn && !seat && mine && (
          <span data-testid={`club-event-registered-${ev.event_id}`}
            className="club-badge px-4 py-2 text-[10px] uppercase tracking-[0.18em]">You're in</span>
        )}
        {signedIn && !seat && !mine && inst && (
          <button type="button" disabled={busy} onClick={() => onRegister(ev.event_id)}
            data-testid={`club-event-cta-${ev.event_id}`}
            className="club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em] disabled:opacity-60">
            <Ticket className="w-3.5 h-3.5" /> {busy ? "One moment" : "Register"}
          </button>
        )}
        {startable && (
          <button type="button" disabled={busy} onClick={() => onStart(ev.event_id)} data-testid={`club-event-start-${ev.event_id}`}
            className="club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em] disabled:opacity-60">
            <Play className="w-3.5 h-3.5" /> {busy ? "One moment" : "Start"}
          </button>
        )}
        {waiting && (
          <span data-testid={`club-event-opens-${ev.event_id}`}
            className="club-badge px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-center leading-tight">
            Rego opens<br />{opensAt ? new Date(opensAt - 60 * 60 * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "soon"}
          </span>
        )}
      </div>
    </div>
  );
};

// What this club has running in Players Room right now.
const RingRow = ({ t }) => (
  <div data-testid={`club-ring-${t.id}`} className="club-row px-4 py-3 flex items-center justify-between gap-4">
    <div className="flex flex-col min-w-0">
      <span className="club-display text-base uppercase leading-none">{GAME_LABELS[t.game] || t.game}</span>
      <span className="text-[11px] club-soft">Ring game · {t.stakes} · {t.seated}/{t.max_seats} seated</span>
    </div>
    <Link to={`/table/${t.id}`} data-testid={`club-ring-join-${t.id}`}
      className="club-btn flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.18em] shrink-0">
      <Play className="w-3.5 h-3.5" /> Join
    </Link>
  </div>
);

const KINDS = [["ALL", "All"], ["RING", "Ring"], ["MTT", "MTT"]];

export const GamesPanel = ({ events: allEvents, games: allGames, signedIn, short, mtts = {}, onRegister, onStart, rings: allRings = [], busy }) => {
  const [kind, setKind] = useState("ALL");
  const showMtt = kind !== "RING";
  const events = showMtt ? allEvents : [];
  const games = showMtt ? allGames : [];
  const rings = kind !== "MTT" ? allRings : [];
  const total = allGames.length + allEvents.length + allRings.length;
  return (
  <Panel title="Playing now" note={total ? `${total} open` : "Built to the club's order"}
    className="shrink-0" bodyClass="club-games-body" testId="club-games">
    {allRings.length > 0 && (allEvents.length + allGames.length) > 0 && (
      <div className="px-4 pt-3 flex gap-2" data-testid="club-games-filter">
        {KINDS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`club-games-filter-${k.toLowerCase()}`}
            className={`club-chip px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-opacity ${kind === k ? "club-chip-on" : "opacity-55 hover:opacity-90"}`}>
            {l} <span className="opacity-60">{k === "ALL" ? total : k === "RING" ? allRings.length : allEvents.length + allGames.length}</span>
          </button>
        ))}
      </div>
    )}
    {games.map((g, i) => <ClubGameRow key={i} g={g} i={i} signedIn={signedIn} />)}
    {events.map((ev) => (
      <ClubEventRow key={ev.event_id} ev={ev} mtt={mtts[ev.event_id]} signedIn={signedIn}
        onRegister={onRegister} onStart={onStart} busy={busy === ev.event_id} />
    ))}
    {rings.length > 0 && kind === "ALL" && <div className="px-4 pt-3 text-[10px] uppercase tracking-[0.25em] club-accent-text" data-testid="club-rings-head">Ring games</div>}
    {rings.map((t) => <RingRow key={t.id} t={t} />)}
    {!events.length && !games.length && !rings.length && (
      <div className="text-sm club-soft" data-testid="club-games-empty">
        No games are open in this room yet. {short}'s tournaments are built to their order and appear here the
        moment the club signs them off.
      </div>
    )}
  </Panel>
  );
};

// The floor space below the games: held for club / Players Room / partner advertising.
export const AdSlot = ({ club }) => (
  <div className="club-ad flex-1 min-h-0 flex items-center justify-center relative" data-testid="club-ad-slot"
    style={club?.ad ? { background: club.ad.bg || "#000" } : undefined}>
    <span className="club-bulbs club-bulbs-top" />
    {club?.ad ? (
      <a href={club.ad.href} target="_blank" rel="noopener noreferrer" data-testid="club-ad-link" title={club.ad.label}
        className="h-full w-full flex items-center justify-center transition-opacity hover:opacity-85">
        <img src={club.ad.src} alt={club.ad.label} data-testid="club-ad-image" className="max-h-full max-w-[92%] object-contain py-2" />
      </a>
    ) : <div className="flex flex-col items-center gap-1 opacity-45">
      <span className="club-display text-sm uppercase tracking-[0.3em]">Advertising space</span>
      <span className="text-[10px] uppercase tracking-[0.22em] club-soft">{club?.short || "Club"} · Players Room · partners</span>
    </div>}
  </div>
);

// Where the club runs live poker, day by day.
export const WeekPanel = ({ venues, region }) => {  const today = TODAY();
  const byDay = Object.fromEntries(DAYS.map(([k]) => [k, []]));
  const any = [];
  (venues || []).forEach((v) => (byDay[v.day] ? byDay[v.day].push(v) : any.push(v)));
  return (
    <Panel title="The week" note={region} className="flex-1" testId="club-week">
      {DAYS.map(([key, label]) => (
        <div key={key} data-testid={`club-day-${key}`}
          className={`club-row px-3 py-2 flex flex-col gap-1 ${key === today ? "club-day-today" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="club-display text-xs uppercase tracking-[0.18em]">{label}</span>
            {key === today && <LiveLamp label="Tonight" />}
          </div>
          {!!byDay[key].length && (
            <div className="club-week-grid text-[8.5px] uppercase tracking-[0.14em] club-soft pb-0.5"
              data-testid={`club-day-head-${key}`}>
              <span />
              <span className="text-right">Rego</span>
              <span className="text-right">Start</span>
              <span className="text-right">Late rego</span>
            </div>
          )}
          {byDay[key].map((v, n) => (
            <div key={n} data-testid={`club-venue-${key}-${n}`} className="flex flex-col">
              <div className="club-week-grid items-baseline">
                <span className="text-[13px] leading-tight truncate">{v.name}</span>
                <span className="club-display text-[11px] club-accent-text text-right">{v.rego || "—"}</span>
                <span className="club-display text-[11px] club-accent-text text-right">{v.time || "—"}</span>
                <span className="club-display text-[11px] club-accent-text text-right">{v.late || "—"}</span>
              </div>
              {(v.buyin || v.prize || v.blinds || v.note) && (
                <span className="text-[10px] club-soft truncate" data-testid={`club-venue-meta-${key}-${n}`}>
                  {[v.buyin, v.prize, v.blinds && `Blinds ${v.blinds}`, v.note].filter(Boolean).join(" · ")}
                </span>
              )}
            </div>
          ))}
          {!byDay[key].length && <span className="text-[11px] club-soft">No game listed</span>}
        </div>
      ))}
      {any.map((v, n) => (
        <div key={`any-${n}`} data-testid={`club-venue-any-${n}`} className="club-row px-3 py-2 flex flex-col">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[13px]">{v.name}</span>
            <span className="club-display text-[11px] club-accent-text">{v.time || "When they run it"}</span>
          </div>
          <span className="text-[10px] club-soft">{[v.buyin, v.prize, v.note].filter(Boolean).join(" · ")}</span>
        </div>
      ))}
    </Panel>
  );
};

// News, offers and series the club has put on the wall.
export const NoticePanel = ({ notices }) => (
  <Panel title="On the board" note="News · offers · series" className="flex-1" testId="club-board">
    {(notices || []).map((n, i) => (
      <div key={i} data-testid={`club-notice-${i}`} className="club-row px-3 py-2 flex flex-col gap-1">
        <span className="club-badge self-start px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]">
          {(n.kind || "NEWS").toLowerCase()}
        </span>
        <span className="club-display text-sm uppercase leading-tight club-neon">{n.title}</span>
        {n.body && <span className="text-[11px] club-soft whitespace-pre-line">{n.body}</span>}
      </div>
    ))}
    {!(notices || []).length && (
      <span className="text-xs club-soft" data-testid="club-notices-empty">
        Nothing on the board this week.
      </span>
    )}
  </Panel>
);

// A wall-mounted screen: club graphics on a bezelled panel that zooms on hover so the detail can be read.
// 2D, but with depth — frame, glass sheen and a shadow thrown onto the wall behind it.
export const ClubScreen = ({ label, slides, origin = "top right", heightClass = "", interval = 7000, testId }) => {
  const list = (slides || []).filter(Boolean);
  const [i, setI] = useState(0);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (hover || list.length < 2) return;
    const id = setInterval(() => setI((n) => (n + 1) % list.length), interval);
    return () => clearInterval(id);
  }, [hover, list.length, interval]);
  const slide = list[Math.min(i, Math.max(list.length - 1, 0))];

  return (
    <div className={`club-screen-wrap shrink-0 ${heightClass}`} data-testid={testId}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="club-screen" style={{ transformOrigin: origin, transform: hover ? "scale(1.34)" : "scale(1)", zIndex: hover ? 50 : 1 }}>
        <div className="club-screen-bezel">
          <div className="club-screen-glass flex items-center justify-center">
            {slide
              ? (slide.src
                  ? <img key={slide.src} src={slide.src} alt={slide.label || label}
                      data-testid={`${testId}-slide-${i}`}
                      className={`w-full h-full club-rise ${slide.fit === "cover" ? "object-cover" : "object-contain p-2"}`} />
                  : <div key={i} className="flex flex-col gap-1 p-4 club-rise" data-testid={`${testId}-slide-${i}`}>
                      {slide.kind && <span className="club-badge self-start px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]">{slide.kind.toLowerCase()}</span>}
                      <span className="club-display text-base uppercase leading-tight club-neon">{slide.title}</span>
                      {slide.body && <span className="text-[11px] club-soft">{slide.body}</span>}
                    </div>)
              : (
                <div className="flex flex-col items-center justify-center gap-2 club-slot">
                  <ImagePlus className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-[0.25em]">Screen</span>
                  <span className="text-[10px] club-soft">Club uploads this</span>
                </div>
              )}
            {list.length > 1 && (
              <div className="club-screen-dots flex justify-center gap-1.5">
                {list.map((_, n) => (
                  <button key={n} onClick={() => setI(n)} data-testid={`${testId}-dot-${n}`} aria-label={`Screen ${n + 1}`}
                    className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--c-accent)", opacity: n === i ? 1 : 0.35 }} />
                ))}
              </div>
            )}
          </div>
          <span className="club-screen-label">{label}</span>
        </div>
      </div>
    </div>
  );
};
